import React from 'react';

// Utils
import { getViewBox } from '../../../../utils/svgUtils';
import { getSvgPathFromStroke } from '../utils/drawingSvgPathUtils';
import { getPathStroke, getStrokeColor, getStrokeColorWithFallback, type DrawingPath } from '../drawingEditorUtils';
import DrawingPathSvg from './DrawingPathSvg';

/**
 * This is used in drawingEditorElementContentUtils.tsx to render the SVG
 * string that will be saved to the element.
 * So it's kept as simple as possible.
 */
type DrawingSvgRendererProps = {
    paths: DrawingPath[];
    width: number;
    height: number;
    useFallbackColor?: boolean;
};

const DrawingSvgRenderer: React.FC<DrawingSvgRendererProps> = ({ paths, width, height, useFallbackColor }) => {
    const getStrokeColorFn = useFallbackColor ? getStrokeColorWithFallback : getStrokeColor;

    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox={getViewBox({ width, height })}>
            {paths.map((path, index) => {
                const { id, color, thinning, size, svgPath, isStrokeOnly } = path;

                if (svgPath) {
                    return (
                        <DrawingPathSvg
                            key={id || `path-${index}`}
                            color={color}
                            getStrokeColor={getStrokeColorFn}
                            isStrokeOnly={isStrokeOnly}
                            size={size}
                            svgPath={svgPath}
                        />
                    );
                }

                const renderedStroke = getPathStroke(path);
                const isDerivedStrokeOnly = thinning === 0;
                const derivedSvgPath = getSvgPathFromStroke(renderedStroke, !isDerivedStrokeOnly);

                return (
                    <DrawingPathSvg
                        key={id || `path-${index}`}
                        color={color}
                        getStrokeColor={getStrokeColorFn}
                        isStrokeOnly={isDerivedStrokeOnly}
                        size={size}
                        svgPath={derivedSvgPath}
                    />
                );
            })}
        </svg>
    );
};

export default DrawingSvgRenderer;
